import { useState, useEffect } from 'react';
import { Transaction } from '../../../types/dashboard';

interface Agency {
  name: string;
  company: string;
}

export function useValidationFilters(
  transactions: Transaction[],
  agencies: Agency[],
  setFilteredTransactions: (transactions: Transaction[]) => void
) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string[]>([]);
  const [selectedAgency, setSelectedAgency] = useState<string[]>([]);
  const [selectedNegotiator, setSelectedNegotiator] = useState<string[]>([]);
  const [showRegulOnly, setShowRegulOnly] = useState(false);
  const [showClosedAndCancelled, setShowClosedAndCancelled] = useState(false);
  const [encaissementDateRange, setEncaissementDateRange] = useState<{
    startDate: string | null;
    endDate: string | null;
  }>({ startDate: null, endDate: null });
  const [appliedEncaissementDateRange, setAppliedEncaissementDateRange] = useState<{
    startDate: string | null;
    endDate: string | null;
  }>({ startDate: null, endDate: null });
  const [compromiseDateRange, setCompromiseDateRange] = useState<{
    startDate: string | null;
    endDate: string | null;
  }>({ startDate: null, endDate: null });
  const [appliedCompromiseDateRange, setAppliedCompromiseDateRange] = useState<{
    startDate: string | null;
    endDate: string | null;
  }>({ startDate: null, endDate: null });

  useEffect(() => {
    filterTransactions();
  }, [
    transactions, 
    searchTerm, 
    selectedStatus, 
    selectedCompany, 
    selectedAgency, 
    selectedNegotiator, 
    showRegulOnly, 
    showClosedAndCancelled,
    appliedEncaissementDateRange,
    appliedCompromiseDateRange
  ]);

  const filterTransactions = () => {
    let filtered = [...transactions];

    // Recherche textuelle
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(t => 
        t.property_number.toLowerCase().includes(searchLower) ||
        t.seller.toLowerCase().includes(searchLower) ||
        t.buyer.toLowerCase().includes(searchLower) ||
        t.agencies.some(agency => agency.toLowerCase().includes(searchLower)) ||
        t.negotiators.some(n => n.name.toLowerCase().includes(searchLower))
      );
    }

    // Filtre par statut
    if (selectedStatus.length > 0) {
      filtered = filtered.filter(t => selectedStatus.includes(t.status));
    }

    // Filtre par société
    if (selectedCompany.length > 0) {
      filtered = filtered.filter(t => 
        t.agencies.some(agencyName => {
          const agencyData = agencies.find(a => a.name === agencyName);
          return agencyData && selectedCompany.includes(agencyData.company);
        })
      );
    }

    // Filtre par agence
    if (selectedAgency.length > 0) {
      filtered = filtered.filter(t => 
        t.agencies.some(agency => selectedAgency.includes(agency))
      );
    }

    // Filtre par négociateur
    if (selectedNegotiator.length > 0) {
      filtered = filtered.filter(t => 
        t.negotiators.some(n => selectedNegotiator.includes(n.name))
      );
    }

    // Filtre par date d'encaissement
    if (appliedEncaissementDateRange.startDate || appliedEncaissementDateRange.endDate) {
      console.log('🔍 Applying encaissement date filter:', appliedEncaissementDateRange);
      console.log('📊 Transactions before encaissement filter:', filtered.length);
      
      filtered = filtered.filter(t => {
        console.log('🔎 Checking transaction:', {
          property_number: t.property_number,
          encaissement_date: t.encaissement_date,
          status: t.status
        });
        
        // ❌ Si aucune date d'encaissement n'est définie, exclure la transaction du filtre
        if (!t.encaissement_date) {
          console.log('❌ Transaction excluded: no encaissement_date');
          return false;
        }
        
        const encaissementDate = new Date(t.encaissement_date);
        console.log('📅 Encaissement date parsed:', encaissementDate);
        
        // Vérifier la date de début
        if (appliedEncaissementDateRange.startDate) {
          const startDate = new Date(appliedEncaissementDateRange.startDate);
          console.log('🔍 Checking start date:', startDate, 'vs', encaissementDate);
          if (encaissementDate < startDate) {
            console.log('❌ Transaction excluded: before start date');
            return false;
          }
        }
        
        // Vérifier la date de fin
        if (appliedEncaissementDateRange.endDate) {
          const endDate = new Date(appliedEncaissementDateRange.endDate);
          endDate.setHours(23, 59, 59, 999); // Inclure toute la journée
          console.log('🔍 Checking end date:', endDate, 'vs', encaissementDate);
          if (encaissementDate > endDate) {
            console.log('❌ Transaction excluded: after end date');
            return false;
          }
        }
        
        console.log('✅ Transaction included in filter');
        return true;
      });
      
      console.log('📊 Transactions after encaissement filter:', filtered.length);
    }

    // Filtre par date de compromis
    if (appliedCompromiseDateRange.startDate || appliedCompromiseDateRange.endDate) {
      console.log('🔍 Applying compromise date filter:', appliedCompromiseDateRange);
      console.log('📊 Transactions before compromise filter:', filtered.length);
      
      filtered = filtered.filter(t => {
        console.log('🔎 Checking transaction:', {
          property_number: t.property_number,
          compromise_date: t.compromise_date,
          status: t.status
        });
        
        // ❌ Si aucune date de compromis n'est définie, exclure la transaction du filtre
        if (!t.compromise_date) {
          console.log('❌ Transaction excluded: no compromise_date');
          return false;
        }
        
        const compromiseDate = new Date(t.compromise_date);
        console.log('📅 Compromise date parsed:', compromiseDate);
        
        // Vérifier la date de début
        if (appliedCompromiseDateRange.startDate) {
          const startDate = new Date(appliedCompromiseDateRange.startDate);
          console.log('🔍 Checking start date:', startDate, 'vs', compromiseDate);
          if (compromiseDate < startDate) {
            console.log('❌ Transaction excluded: before start date');
            return false;
          }
        }
        
        // Vérifier la date de fin
        if (appliedCompromiseDateRange.endDate) {
          const endDate = new Date(appliedCompromiseDateRange.endDate);
          endDate.setHours(23, 59, 59, 999); // Inclure toute la journée
          console.log('🔍 Checking end date:', endDate, 'vs', compromiseDate);
          if (compromiseDate > endDate) {
            console.log('❌ Transaction excluded: after end date');
            return false;
          }
        }
        
        console.log('✅ Transaction included in filter');
        return true;
      });
      
      console.log('📊 Transactions after compromise filter:', filtered.length);
    }

    // Filtre régularisation
    if (showRegulOnly) {
      filtered = filtered.filter(t => t.regul);
    }

    // Filtre transactions clôturées et annulées
    if (!showClosedAndCancelled) {
      filtered = filtered.filter(t => !['cloture', 'annule'].includes(t.status));
    }

    setFilteredTransactions(filtered);
  };

  const applyEncaissementDateFilter = () => {
    setAppliedEncaissementDateRange(encaissementDateRange);
  };

  const clearEncaissementDateFilter = () => {
    setEncaissementDateRange({ startDate: null, endDate: null });
    setAppliedEncaissementDateRange({ startDate: null, endDate: null });
  };

  const applyCompromiseDateFilter = () => {
    setAppliedCompromiseDateRange(compromiseDateRange);
  };

  const clearCompromiseDateFilter = () => {
    setCompromiseDateRange({ startDate: null, endDate: null });
    setAppliedCompromiseDateRange({ startDate: null, endDate: null });
  };

  return {
    searchTerm,
    selectedStatus,
    selectedCompany,
    selectedAgency,
    selectedNegotiator,
    showRegulOnly,
    showClosedAndCancelled,
    encaissementDateRange,
    appliedEncaissementDateRange,
    compromiseDateRange,
    appliedCompromiseDateRange,
    applyEncaissementDateFilter,
    clearEncaissementDateFilter,
    applyCompromiseDateFilter,
    clearCompromiseDateFilter,
    setSearchTerm,
    setSelectedStatus,
    setSelectedCompany,
    setSelectedAgency,
    setSelectedNegotiator,
    setShowRegulOnly,
    setShowClosedAndCancelled,
    setEncaissementDateRange,
    setCompromiseDateRange,
    setAppliedCompromiseDateRange
  };
}